import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  IBucketableRule,
  IBucketPayer,
  IPayerOption,
  IRuleBucketMember,
  toBucketMembers,
} from "../helpers/ruleBucket";

/**
 * CLE-2842 — "bucket mode" for the MPPR / Case Rate / Package edit pages.
 *
 * A bucket is the set of rows that are the same rule for the same provider and differ only in
 * payer. When the rule being edited is in a bucket of two or more, the page offers to apply the
 * edit to every payer at once (one atomic group request) and to add / remove payers. The provider
 * section, the submit button and the case-rate conflict check all read this context; outside a
 * provider, or for a lone rule, `useRuleBucket()` is null and they behave exactly as before.
 */
export interface IRuleBucket {
  groupKey: string;
  /** Every current row of the bucket, sorted by payer name. */
  members: IRuleBucketMember[];
  /**
   * The "Apply changes to all N payers" switch. Off = edit only this row, as before; turning it
   * off also drops any payer additions and removals, so they cannot come back unseen.
   */
  applyToAll: boolean;
  setApplyToAll: (value: boolean) => void;
  /** Member ids to delete on save. */
  removedIds: string[];
  removeMember: (id: string) => void;
  /** Payers to copy the rule to on save. */
  addedPayers: IPayerOption[];
  addPayer: (payer: IPayerOption) => void;
  removeAddedPayer: (payerId: number) => void;
  /** Members that survive the save. */
  activeMembers: IRuleBucketMember[];
  /**
   * Every payer the rule will cover after the save: surviving members + added payers. A member
   * on All payers is `value: null`.
   */
  activePayers: IBucketPayer[];
}

const RuleBucketContext = createContext<IRuleBucket | null>(null);

export function useRuleBucket(): IRuleBucket | null {
  return useContext(RuleBucketContext);
}

interface IRuleBucketProviderProps {
  /** The row being edited, as loaded by id — its `bucketMembers` define the bucket. */
  row: IBucketableRule | undefined;
  children: ReactNode;
}

export function RuleBucketProvider({
  row,
  children,
}: IRuleBucketProviderProps) {
  const members = useMemo(() => toBucketMembers(row), [row]);
  const [applyToAll, setApplyToAllState] = useState(true);
  const [removedIds, setRemovedIds] = useState<string[]>([]);
  const [addedPayers, setAddedPayers] = useState<IPayerOption[]>([]);

  const setApplyToAll = useCallback((value: boolean) => {
    setApplyToAllState(value);
    if (!value) {
      setRemovedIds([]);
      setAddedPayers([]);
    }
  }, []);

  const value = useMemo<IRuleBucket | null>(() => {
    if (!row?.groupKey || members.length <= 1) return null;
    const activeMembers = members.filter(
      (member) => !removedIds.includes(member.id)
    );
    return {
      groupKey: row.groupKey,
      members,
      applyToAll,
      setApplyToAll,
      removedIds,
      removeMember: (id: string) =>
        setRemovedIds((ids) => (ids.includes(id) ? ids : [...ids, id])),
      addedPayers,
      addPayer: (payer: IPayerOption) => {
        // Re-adding a payer whose row was marked for removal just keeps that row.
        const removedMember = members.find(
          (member) =>
            member.payerId === payer.value && removedIds.includes(member.id)
        );
        if (removedMember) {
          setRemovedIds((ids) => ids.filter((id) => id !== removedMember.id));
          return;
        }
        setAddedPayers((payers) =>
          payers.some((p) => p.value === payer.value)
            ? payers
            : [...payers, payer]
        );
      },
      removeAddedPayer: (payerId: number) =>
        setAddedPayers((payers) => payers.filter((p) => p.value !== payerId)),
      activeMembers,
      activePayers: [
        ...activeMembers.map((member) => ({
          name: member.payerName,
          value: member.payerId,
        })),
        ...addedPayers,
      ],
    };
  }, [
    row?.groupKey,
    members,
    applyToAll,
    setApplyToAll,
    removedIds,
    addedPayers,
  ]);

  return (
    <RuleBucketContext.Provider value={value}>
      {children}
    </RuleBucketContext.Provider>
  );
}
